import { NgComponentOutlet } from '@angular/common';
import { Component, InjectionToken, Type, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, LazyModuleInjector, UserService } from '@cfp/core';
import { DocumentsCard } from '@cfp/features-shared/documents';
import { EnergyLabelRequirementStatus } from '@cfp/features-shared/energy-label-requirement';
import { NotesButton, NotesCard } from '@cfp/features-shared/notes';
import { BuildingPdfDownloadButton } from '@cfp/shared/building/pdf-download-button';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { BuildingDetailsDTO, BuildingTableItemDTO } from 'csr-asset-app';
import { EnergyLabelRequirementDTO } from 'csr-energy-label-requirement';
import { BuildingHeader } from './building-header';

export const CUSTOM_BUILDING_HEADER_COMPONENT = new InjectionToken<Type<Component>>('Custom building header component');

@Component({
  selector: 'cfp-building-details-header',
  templateUrl: './building-details-header.html',
  imports: [
    BuildingHeader,
    EnergyLabelRequirementStatus,
    BuildingPdfDownloadButton,
    NotesButton,
    NotesCard,
    DocumentsCard,
    NgComponentOutlet
  ]
})
export class BuildingDetailsHeader {
  private readonly environment = inject(ENVIRONMENT);
  private readonly lazyModuleInjector = inject(LazyModuleInjector);

  public readonly building = input.required<BuildingDetailsDTO | BuildingTableItemDTO | undefined>();
  public readonly combineNameAndAddress = input<boolean>(false);
  public readonly hideSelect = input<boolean>(false);
  public readonly hideDropdown = input<boolean>(false);
  public readonly hideGoToAllBuildings = input<boolean>(false);
  public readonly hideDeleteBuilding = input<boolean>(false);
  public readonly hideManageBuildingTags = input<boolean>(this.environment.components.hideManageBuildingTags === true);
  public readonly canDownloadReport = input<boolean>(
    this.environment.components.buildingHeaderShowDownloadReport === true
  );
  public readonly showDownloadReportButton = input<boolean>(false);
  public readonly buildingHeaderClass = input<string>('col-12 ms-lg-auto col-lg-auto mt-3 mt-lg-0 mb-5 mb-lg-0');
  public readonly customBuildingImageUrl = input<string>();
  public readonly energyLabelRequirement = input<EnergyLabelRequirementDTO>();
  public readonly onlyAllowNgContent = input<boolean>(false);

  protected customBuildingHeaderComponent: Type<Component> | null = null;

  protected readonly displayAddress = computed(() => {
    const building = this.building();
    if (!building) {
      return undefined;
    }
    if ('address' in building) {
      return building.address?.display;
    } else if ('displayAddress' in building) {
      return building.displayAddress;
    }
  });

  protected readonly isMonument = computed(() => {
    const building = this.building();
    if (!building) {
      return false;
    }
    if ('address' in building) {
      return building.address?.monument === true;
    } else if ('monument' in building) {
      return building.monument === true;
    }
    return false;
  });

  protected readonly countryCode = computed(() => {
    const building = this.building();
    if (!building) {
      return undefined;
    }
    if ('address' in building) {
      return building.address?.countryCode;
    } else if ('countryCode' in building) {
      return building.countryCode;
    }
  });

  protected readonly portfolioId = computed(() => {
    const building = this.building();
    if (building?.portfolio && 'id' in building?.portfolio) {
      return building.portfolio.id;
    }
  });

  protected readonly labelGroup = computed(() => this.labelGroupService.getStoredPreferredLabelGroup());
  protected readonly url = computed(() => {
    const building = this.building();
    if (building?.portfolio && 'id' in building?.portfolio) {
      return `/report/${building.portfolio.id}/buildings/${building.id}`;
    }
  });
  protected readonly fileName = computed(() => this.displayAddress()?.join('-').replace(/,/g, '').replace(/ /g, '-'));

  private readonly userService = inject(UserService);
  private readonly labelGroupService = inject(LabelGroupService);

  protected readonly hasEnergyLabelRequirementLicense = toSignal(
    this.userService.hasLicense('energy_label_requirement')
  );

  protected readonly hasBuildingNotesLicense = toSignal(this.userService.hasLicense('building_notes'));
  protected readonly hasBuildingDocumentsLicense = toSignal(this.userService.hasLicense('building_documents'));

  protected readonly showNotesAsCard = this.environment.components.showNotesAsCard === true;

  constructor() {
    this.customBuildingHeaderComponent = this.lazyModuleInjector.get(CUSTOM_BUILDING_HEADER_COMPONENT);
  }
}
